import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BinMid, CheckCircleMid, CheckCircleFilledMid } from '@zooplus/icons';

import CardIcon from '../CardIcon/CardIcon';

import { CreditCardData } from './index.interfaces';
import MaskedCardNumber from './MaskedCardNumber';
import styles from './StoredCreditCards.module.scss';

export type CreditCardProps = {
  creditCard: CreditCardData;
  onDelete?: React.MouseEventHandler;
  onSelect?: React.MouseEventHandler;
  showIcons: boolean;
  loadingCardPspIdentifier?: string;
};

/**
 * Display the tile holding all the credit card information and the buttons to interact with it.
 */
const CreditCard: React.FC<CreditCardProps> = ({
  creditCard,
  onDelete,
  onSelect,
  showIcons,
  loadingCardPspIdentifier,
}) => {
  const { t } = useTranslation();
  const isSessionCard = creditCard.pspIdentifier === '0';

  return (
    <div className={styles.creditCardContainer}>
      <div
        className={classNames(
          styles.creditCard,
          creditCard.selected && styles.selected,
          creditCard.expired && styles.expired,
        )}
        onClick={onSelect}
        data-zta={
          isSessionCard ? 'storedSessionCC' : creditCard.selected ? 'storedSelectedCC' : 'storedCC'
        }>
        <div className={styles.creditCardHeader}>
          <div>{<CardIcon data-zta="ccIcon" type={creditCard.brand} />}</div>
          {showIcons && (onDelete || !isSessionCard) && (
            <div>
              {onDelete && (
                <span className={styles.delete} data-zta="removeCC" onClick={onDelete}>
                  <BinMid />
                </span>
              )}
              {!isSessionCard && (
                <span data-zta="checkedCCIcon" className={styles.validity}>
                  {creditCard.selected ? <CheckCircleFilledMid /> : <CheckCircleMid />}
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          <div className={styles.holderName}>{creditCard.holderName ?? '-'}</div>
          <div>
            <MaskedCardNumber lastFourDigits={creditCard.lastFourDigits} />
          </div>
          <div className={styles.expiration}>
            <span>
              {creditCard.expirationMonth ? (
                <span className={styles.month}>{creditCard.expirationMonth}</span>
              ) : (
                <span className={styles.creditCardDots}>
                  <span className={styles.creditCardDot}>&#11044;</span>
                  <span className={styles.creditCardDot}>&#11044;</span>
                </span>
              )}
              <span className={styles.separator}>/</span>
              {creditCard.expirationYear ? (
                <span className={styles.year}>{creditCard.expirationYear}</span>
              ) : (
                <span className={styles.creditCardDots}>
                  <span className={styles.creditCardDot}>&#11044;</span>
                  <span className={styles.creditCardDot}>&#11044;</span>
                </span>
              )}
            </span>
            {creditCard.expired && (
              <>
                {' '}
                <span className={styles.expiredLabel}>
                  {creditCard.expired && t('checkout.payment.creditcard.expired.message')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {loadingCardPspIdentifier && (
        <div className={styles.loaderBackdrop}>
          {loadingCardPspIdentifier === creditCard.pspIdentifier && (
            <div className={styles.loaderLarge} />
          )}
        </div>
      )}
    </div>
  );
};

export default CreditCard;
